import React, { useState, useRef, useEffect, MutableRefObject } from 'react';

interface SplitPaneOptions {
    defaultPercent?: number;
    minPercent?: number;
    maxPercent?: number;
}

export interface SplitPaneApi {
    leftWidth: string;
    containerRef: MutableRefObject<HTMLDivElement | null>;
    startDrag: (e: React.MouseEvent | React.TouchEvent) => void;
}

export function useSplitPane({
    defaultPercent = 30,
    minPercent = 12,
    maxPercent = 75,
}: SplitPaneOptions = {}): SplitPaneApi {
    const [leftWidth, setLeftWidth] = useState(`${defaultPercent}%`);
    const isDragging = useRef(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        function onMove(e: MouseEvent | TouchEvent): void {
            if (!isDragging.current) return;
            const container = containerRef.current;
            if (!container) return;
            const rect = container.getBoundingClientRect();
            const clientX = 'touches' in e && e.touches?.length
                ? e.touches[0].clientX
                : (e as MouseEvent).clientX;
            let pct = ((clientX - rect.left) / rect.width) * 100;
            if (pct < minPercent) pct = minPercent;
            if (pct > maxPercent) pct = maxPercent;
            setLeftWidth(pct.toFixed(2) + '%');
        }

        function onUp(): void {
            isDragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        window.addEventListener('mousemove', onMove);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchend', onUp);

        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchend', onUp);
        };
    }, [minPercent, maxPercent]);

    function startDrag(e: React.MouseEvent | React.TouchEvent): void {
        isDragging.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        e.preventDefault();
    }

    return { leftWidth, containerRef, startDrag };
}
